import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import type { Rights } from '../lib/session.js';
import { EmptyState, ErrorState, Loading, PageHeader, Panel, type Tone } from '../components/ui.js';
import { Badge } from '../components/2b-status.js';
import '../screens-2b.css';

interface ChannelItem {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  endpoint: 'transactional' | 'custom' | 'list_trigger';
  templateKey: string;
  templateStatus: 'synced' | 'not_synced' | 'missing';
  providerEmailId: string | null;
}

const ENDPOINT_LABEL: Record<ChannelItem['endpoint'], string> = {
  transactional: 'Přímo z hubu (servisní e-mail)',
  custom: 'Šablona ve SmartEmailingu (marketing)',
  list_trigger: 'Přes seznam kontaktů (záložní cesta)',
};
const STATUS: Record<ChannelItem['templateStatus'], { tone: Tone; text: string }> = {
  synced: { tone: 'ok', text: 'Nahráno' },
  not_synced: { tone: 'warn', text: 'Nenahráno' },
  missing: { tone: 'bad', text: 'Chybí šablona' },
};

/** Matice Kanály: jak která zpráva odchází a jestli je její šablona ve SmartEmailingu. */
export function Channels({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState('');
  const query = useQuery<{ items: ChannelItem[] }>({
    queryKey: ['channels', brand],
    queryFn: () => api.get(`/api/channels${qs({ brand })}`),
  });
  const sync = useMutation({
    mutationFn: (key: string) =>
      api.post<{ ok: boolean; error?: string }>(
        `/api/templates/${encodeURIComponent(key)}/sync`,
        {},
      ),
    onSuccess: (res) => {
      setError(res.ok ? null : (res.error ?? 'Nahrání se nepodařilo.'));
      void client.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <Loading what="kanály" />;
  if (query.isError)
    return (
      <ErrorState
        title="Kanály se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const canSync = rights.can('map_templates');

  return (
    <>
      <PageHeader
        level={2}
        title="Kanály"
        description="Jak která zpráva odchází ke klientovi a jestli je její šablona nahraná ve SmartEmailingu."
      />
      {error ? <ErrorState title="Šablonu se nepodařilo nahrát" error={new Error(error)} /> : null}
      <div className="help">
        <p>
          Servisní zprávy (kódy S…) odcházejí přímo z hubu jako hotové HTML a nesmí obsahovat
          reklamu. Marketingové (kódy M… a H…) jdou přes šablonu uloženou ve SmartEmailingu; hub ji
          nahraje při publikaci a při odeslání do ní dosadí aktuální data. Transakční e-maily z
          e-shopu (například potvrzení objednávky) hub neposílá, uvidíte je jen v záložce Odeslané.
        </p>
      </div>
      <Panel
        title="Kanály zpráv"
        action={<BrandSelect value={brand} onChange={setBrand} allLabel="První značka" shopOnly />}
      >
        {(query.data?.items ?? []).length === 0 ? (
          <EmptyState title="Žádné zprávy">
            Katalog zpráv je prázdný, takže tu není co zobrazit.
          </EmptyState>
        ) : null}
        {(query.data?.items ?? []).length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kód</th>
                  <th>Zpráva</th>
                  <th>Druh</th>
                  <th>Cesta odeslání</th>
                  <th>Šablona</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {(query.data?.items ?? []).map((i) => (
                  <tr key={i.code}>
                    <td>{i.code}</td>
                    <td>{i.name}</td>
                    <td>
                      {i.channel === 'service' ? 'Servis — transakční' : 'Marketing — přes šablonu'}
                    </td>
                    <td>{ENDPOINT_LABEL[i.endpoint]}</td>
                    <td>
                      <Badge tone={STATUS[i.templateStatus].tone}>
                        {STATUS[i.templateStatus].text}
                      </Badge>
                      {i.providerEmailId ? (
                        <span className="note">
                          {' '}
                          ID e-mailu v SmartEmailingu: {i.providerEmailId}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {i.channel === 'marketing' && i.templateStatus !== 'missing' && canSync ? (
                        <button
                          className="btn"
                          disabled={sync.isPending}
                          aria-label={`Nahrát znovu šablonu ${i.code} do SmartEmailingu`}
                          onClick={() => sync.mutate(i.templateKey)}
                        >
                          Nahrát znovu
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
    </>
  );
}
